import { Component, OnInit, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';
import { MetodosgenService } from '../../services/metodosgen.service';

export interface EditField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'number' | 'textarea';
  value: any;
  placeholder?: string;
  required?: boolean;
}

@Component({
  selector: 'app-editmodal',
  templateUrl: './editmodal.component.html',
  styleUrls: ['./editmodal.component.scss'],
  standalone: false
})
export class EditmodalComponent implements OnInit {
  @Input() title: string = 'Editar';
  @Input() subtitle: string = 'Editar Factura';
  @Input() fields: EditField[] = [];
  @Input() showView: boolean = false;
  @Input() viewText: string = 'Ver Factura';
  @Input() onViewClick?: () => void;
  @Input() onCropClick?: () => void;
  @Input() filename: string = '';

  loadingAI = false;
  aiAttemptStatus = 'Llenar formulario con IA';

  imageUrl: string | null = null;
  loadingImage = false;

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosUi: Metodosui,
    private metodosGenService: MetodosgenService
  ) {}

  ngOnInit() {
    // Clone fields to avoid mutating parents directly
    this.fields = this.fields.map(f => ({ ...f }));
    if (this.filename && !this.filename.toLowerCase().endsWith('.pdf')) {
      this.cargarImagenFactura();
    }
  }

  async cargarImagenFactura() {
    this.loadingImage = true;
    try {
      const base64Raw = await firstValueFrom(this.javapiService.getImage(this.filename, 'path_img_facturas'));
      if (base64Raw) {
        this.imageUrl = base64Raw.startsWith('data:') ? base64Raw : `data:image/jpeg;base64,${base64Raw}`;
      }
    } catch (e) {
      console.error('Error cargando preview de factura:', e);
    } finally {
      this.loadingImage = false;
    }
  }

  cerrar() {
    this.modalCtrl.dismiss(null);
  }

  getIcon(key: string): string {
    const k = key.toLowerCase();
    if (k.includes('fecha') || k.includes('date')) return 'calendar-outline';
    if (k.includes('lugar') || k.includes('sitio') || k.includes('dir') || k.includes('ubi')) return 'pin-outline';
    if (k.includes('monto') || k.includes('total') || k.includes('precio') || k.includes('val') || k.includes('pago')) return 'cash-outline';
    return 'document-text-outline';
  }

  ver() {
    if (this.onViewClick) {
      this.onViewClick();
    } else {
      this.modalCtrl.dismiss({ action: 'view' });
    }
  }

  recortarImagen() {
    if (this.onCropClick) {
      this.onCropClick();
    } else {
      this.modalCtrl.dismiss({ action: 'crop' });
    }
  }

  guardar() {
    const result: { [key: string]: any } = {};
    for (const f of this.fields) {
      if (f.required && (f.value === undefined || f.value === null || f.value === '')) {
        return;
      }
      result[f.key] = f.value;
    }
    this.modalCtrl.dismiss({ action: 'save', data: result });
  }

  async llenarConIA() {
    if (!this.filename) {
      this.metodosUi.showToast('No hay archivo asociado para analizar', 'warning');
      return;
    }

    this.loadingAI = true;
    this.aiAttemptStatus = 'Descargando factura...';

    try {
      // 1. Obtener la imagen base64 desde el servidor
      const base64Raw = await firstValueFrom(this.javapiService.getImage(this.filename, 'path_img_facturas'));
      if (!base64Raw) {
        this.metodosUi.showToast('No se pudo descargar la imagen de la factura', 'danger');
        this.loadingAI = false;
        this.aiAttemptStatus = 'Llenar formulario con IA';
        return;
      }

      let base64 = base64Raw.startsWith('data:') ? base64Raw : `data:image/jpeg;base64,${base64Raw}`;

      // Redimensionar la imagen para evitar payloads pesados
      base64 = await this.resizeBase64Image(base64, 1200, 1200);

      // 2. Cargar configuración CONFIG_IA_ANALIZAR_FACTURA
      const paramRes = await firstValueFrom(this.javapiService.getJsonParam('CONFIG_IA_ANALIZAR_FACTURA'));
      let model = undefined;
      let temperature = undefined;
      let systemInstruction = undefined;
      let prompt = undefined;
      let freeAttempts = 2;
      let payAttempts = 1;
      let forcePay = false;

      if (paramRes && paramRes.pa_json) {
        try {
          const config = typeof paramRes.pa_json === 'string' ? JSON.parse(paramRes.pa_json) : paramRes.pa_json;
          model = config.model;
          temperature = config.temperature;
          systemInstruction = config.system_instruction;
          prompt = config.prompt_template;
          
          if (config.free_attempts !== undefined) freeAttempts = Number(config.free_attempts);
          if (config.pay_attempts !== undefined) payAttempts = Number(config.pay_attempts);
          if (config.force_pay !== undefined) forcePay = !!config.force_pay;
        } catch (parseErr) {
          console.error('Error parseando CONFIG_IA_ANALIZAR_FACTURA', parseErr);
        }
      }

      // Reemplazar tokens de fecha actuales para darle contexto de tiempo real a la IA
      const hoyDate = new Date();
      const hoyStr = hoyDate.toISOString().split('T')[0]; // "YYYY-MM-DD"
      const anoStr = String(hoyDate.getFullYear()); // "YYYY"
      if (prompt) {
        prompt = prompt.replace(/{FECHA_HOY}/g, hoyStr).replace(/{ANO_HOY}/g, anoStr);
      }
      if (systemInstruction) {
        systemInstruction = systemInstruction.replace(/{FECHA_HOY}/g, hoyStr).replace(/{ANO_HOY}/g, anoStr);
      }

      // 3. Generar lista de intentos
      const attemptsList: boolean[] = [];
      if (forcePay) {
        for (let i = 0; i < payAttempts; i++) attemptsList.push(true);
      } else {
        for (let i = 0; i < freeAttempts; i++) attemptsList.push(false);
        for (let i = 0; i < payAttempts; i++) attemptsList.push(true);
      }
      if (attemptsList.length === 0) attemptsList.push(false);

      let res = null;
      let success = false;
      const totalAttempts = attemptsList.length;

      for (let idx = 0; idx < totalAttempts; idx++) {
        const usePayKey = attemptsList[idx];
        const attempt = idx + 1;
        this.aiAttemptStatus = `Analizando factura (${attempt}/${totalAttempts})...`;

        try {
          res = await firstValueFrom(
            this.javapiService.analizarFactura(base64, prompt, systemInstruction, model, temperature, usePayKey, attempt, totalAttempts)
          );
          if (res && res.result) {
            success = true;
            break;
          }
        } catch (attemptErr) {
          console.error(`Error en intento ${attempt}:`, attemptErr);
        }

        if (attempt < totalAttempts) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }

      if (!success) {
        throw new Error(res && res.error ? res.error : 'No se pudo obtener respuesta de la IA.');
      }

      if (res && res.result) {
        const data = this.metodosGenService.parsearJsonIa(res.result);
        
        if (!data || (data && !data.monto && !data.fecha && !data.lugar && !data.descripcion)) {
          throw new Error('La respuesta de la IA no contiene datos legibles.');
        }

        // Mapear campos dinámicamente
        const extractedFecha = data.fecha || data.date || data.fecha_factura;
        if (extractedFecha) {
          const field = this.fields.find(f => f.key === 'fecha');
          if (field) field.value = extractedFecha;
        }

        const extractedMonto = data.monto || data.total || data.monto_total || data.amount;
        if (extractedMonto) {
          const field = this.fields.find(f => f.key === 'monto');
          if (field) field.value = extractedMonto;
        }

        const extractedLugar = data.lugar || data.comercio || data.vendor || data.place || data.establishment;
        if (extractedLugar) {
          const field = this.fields.find(f => f.key === 'lugar');
          if (field) field.value = extractedLugar;
        }

        this.metodosUi.showToast('Datos completados con IA con éxito', 'success');
      }
    } catch (e: any) {
      console.error(e);
      this.metodosUi.showToast(e?.message || 'Error al procesar con IA', 'danger');
    } finally {
      this.loadingAI = false;
      this.aiAttemptStatus = 'Llenar formulario con IA';
    }
  }

  resizeBase64Image(base64Str: string, maxWidth = 1200, maxHeight = 1200): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        } else {
          resolve(base64Str);
        }
      };
      img.onerror = () => {
        resolve(base64Str);
      };
    });
  }
}
